"use client";

import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  MoreHorizontal,
  Edit,
  ArrowUpDown,
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../components/ui/shadcn-io/spinner";

// Updated type with off-boarding fields
export type AttendanceEmployee = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  designation?: string;
  department?: string;
  attendanceStatus: "added" | "not_added";
  isActive: boolean;
  resignationDate?: string;
  attendance?: {
    month: number;
    year: number;
    workingDays: number;
    paidLeaves: number;
    unpaidLeaves: number;
  };
};

// Actions cell component with loading states
const AttendanceActionsCell = ({
  employee,
  month,
  year,
}: {
  employee: AttendanceEmployee;
  month: string;
  year: string;
}) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [actionType, setActionType] = useState<string>("");

  const handleAddAttendance = async () => {
    setIsNavigating(true);
    setActionType("add");
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      navigate(`/attendance/add/${employee.id}?month=${month}&year=${year}`);
    } finally {
      setIsNavigating(false);
      setActionType("");
    }
  };

  const handleEditAttendance = async () => {
    setIsNavigating(true);
    setActionType("edit");
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      navigate(`/attendance/edit/${employee.id}?month=${month}&year=${year}`);
    } finally {
      setIsNavigating(false);
      setActionType("");
    }
  };

  return (
    <div className="flex items-center gap-2">
      {employee.attendanceStatus === "not_added" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              disabled={isNavigating}
            >
              <span className="sr-only">Open menu</span>
              {isNavigating && actionType === "add" ? (
                <Spinner variant="ellipsis" size={16} />
              ) : (
                <MoreHorizontal className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleAddAttendance}
              disabled={isNavigating}
            >
              {isNavigating && actionType === "add" ? (
                <Spinner variant="ellipsis" size={16} className="mr-2" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {isNavigating && actionType === "add"
                ? "Loading..."
                : "Add Attendance"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              disabled={isNavigating}
            >
              <span className="sr-only">Open menu</span>
              {isNavigating && actionType === "edit" ? (
                <Spinner variant="ellipsis" size={16} />
              ) : (
                <MoreHorizontal className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleEditAttendance}
              disabled={isNavigating}
            >
              {isNavigating && actionType === "edit" ? (
                <Spinner variant="ellipsis" size={16} className="mr-2" />
              ) : (
                <Edit className="mr-2 h-4 w-4" />
              )}
              {isNavigating && actionType === "edit"
                ? "Loading..."
                : "Edit Attendance"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export const attendanceColumns = (
  month: string,
  year: string
): ColumnDef<AttendanceEmployee>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  // {
  //   accessorKey: "email",
  //   header: ({ column }) => {
  //     return (
  //       <Button
  //         variant="ghost"
  //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  //         className="h-auto p-0 justify-start"
  //       >
  //         Email
  //         <ArrowUpDown className="ml-2 h-4 w-4" />
  //       </Button>
  //     );
  //   },
  //   cell: ({ row }) => {
  //     const email = row.getValue("email") as string;
  //     return <div className="text-muted-foreground text-left">{email}</div>;
  //   },
  // },
  {
    accessorKey: "isActive",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("isActive") as boolean;
      const resignationDate = row.original.resignationDate;

      return (
        <div className="text-left">
          {isActive ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <div className="space-y-1">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
                Inactive
              </span>
              {resignationDate && (
                <div className="text-xs text-muted-foreground">
                  {new Date(resignationDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "attendanceStatus",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Attendance Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue("attendanceStatus") as string;
      const employee = row.original;

      if (status === "added") {
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-green-100 text-green-800">
              Recorded
            </span>
          </div>
        );
      } else {
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-red-100 text-red-800">
              Pending
            </span>
          </div>
        );
      }
    },
    // Sort function to show "not_added" first
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.getValue("attendanceStatus") as string;
      const statusB = rowB.getValue("attendanceStatus") as string;

      if (statusA === "not_added" && statusB === "added") return -1;
      if (statusA === "added" && statusB === "not_added") return 1;
      return 0;
    },
  },
  {
    id: "workingDays",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Working Days
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const employee = row.original;
      if (employee.attendanceStatus === "added" && employee.attendance) {
        return (
          <div className="text-left">
            <span className="font-medium">
              {employee.attendance.workingDays}{" "}
              <span className="text-xs text-muted-foreground">days</span>
            </span>
          </div>
        );
      }
      return <div className="text-muted-foreground text-left">—</div>;
    },
    accessorFn: (row) => row.attendance?.workingDays || 0,
  },
  {
    id: "paidLeaves",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Paid Leaves
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const employee = row.original;
      if (employee.attendanceStatus === "added" && employee.attendance) {
        return (
          <div className="text-left">
            <span className="font-medium text-blue-600">
              {employee.attendance.paidLeaves}{" "}
              <span className="text-xs text-muted-foreground">days</span>
            </span>
          </div>
        );
      }
      return <div className="text-muted-foreground text-left">—</div>;
    },
    accessorFn: (row) => row.attendance?.paidLeaves || 0,
  },
  {
    id: "unpaidLeaves",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Unpaid Leaves
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const employee = row.original;
      if (employee.attendanceStatus === "added" && employee.attendance) {
        return (
          <div className="text-left">
            <span className="font-medium text-red-600">
              {employee.attendance.unpaidLeaves}{" "}
              <span className="text-xs text-muted-foreground">days</span>
            </span>
          </div>
        );
      }
      return <div className="text-muted-foreground text-left">—</div>;
    },
    accessorFn: (row) => row.attendance?.unpaidLeaves || 0,
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const employee = row.original;
      return (
        <AttendanceActionsCell employee={employee} month={month} year={year} />
      );
    },
  },
];
